// ===== Convert ▸ 빌드 검증 sub-tab =====
// Validates an already-converted build against one network's requirements.
// Built up in steps: (1) network picker · (2) upload · (3) preview · (4) checks.

import { escapeHtml, fmtSize } from './util.js';
import { NETWORKS } from './convert-core.js';
import { unzip, findEntryHtml, decodeText } from './unzip.js';
import { rulesFor } from './verify-rules.js';
import { injectProbe } from './verify-probe.js';

const root = document.getElementById('verifyRoot');

// A build targets exactly one network, so this picker is single-select —
// unlike the multi-select one in 네트워크 변환.
let selectedNetwork = null;

// One build at a time — the preview and the checks both describe this one file.
// { name, size, kind: 'html'|'zip', html, entryPath, files: [{name, data}] }
let build = null;
let loadError = null;
// The picker and dropzone fold away once a build is in, so the frame and the
// checklist start at the top of the viewport instead of below a spent form.
let setupOpen = true;

// Preview state. Blob URLs are per-render and must be revoked, or every reload
// leaks the whole build (these are multi-MB documents).
let previewUrls = [];

// Runtime observations from the probe, reset on every preview run. A check the
// build has not exercised yet stays pending — it is never assumed to pass.
let runtime = null;
function freshRuntime() {
  return { booted: false, gesture: false, gestures: 0, ready: new Set(),
           calls: new Set(), callInfo: new Map(),
           audio: [], errors: [], autoplayBlocked: 0, net: [] };
}

function render() {
  root.innerHTML = `
    <!-- Setup collapses to one line once a build is loaded, so the frame and the
         checklist reach the top of the viewport instead of sitting below a
         dropzone the user is done with. -->
    <div class="vf-setup" id="vfSetup">
      <div class="vf-setup-summary">
        <span class="vf-sum-net" id="vfSumNet"></span>
        <span class="vf-sum-file" id="vfSumFile"></span>
        <button class="cv-ghost" id="vfSetupEdit">변경</button>
      </div>

      <div class="vf-setup-full">
        <div class="cv-head">
          <h2>빌드 검증</h2>
          <p>변환이 끝난 빌드 파일이 해당 네트워크 규격을 만족하는지 검사합니다.</p>
        </div>

        <div class="cv-section">
          <div class="cv-section-label"><b>1</b> 검증할 네트워크</div>
          <div class="cv-networks" id="vfNetworks"></div>
        </div>

        <div class="cv-section">
          <div class="cv-section-label"><b>2</b> 빌드 파일</div>
          <div class="cv-drop" id="vfDrop">
            <input type="file" id="vfFileInput" accept=".html,.htm,.zip" hidden>
            <div class="cv-drop-inner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              <div class="cv-drop-text">변환된 빌드를 끌어다 놓거나 <button class="cv-link" id="vfBrowse">찾아보기</button></div>
              <div class="cv-drop-sub">.html · .zip · 한 번에 1개</div>
              <div class="cv-drop-locked">먼저 검증할 네트워크를 선택하세요</div>
            </div>
          </div>
          <div class="cv-filelist" id="vfFile"></div>
        </div>
      </div>
    </div>

    <div class="cv-section vf-workarea">
      <div class="vf-preview-bar">
        <button id="vfReload" class="vf-reload" title="다시 실행">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          <span>새로고침</span>
        </button>
      </div>
      <div class="vf-stage" id="vfStage">
        <div class="vf-frame-wrap">
          <div class="vf-frame" id="vfFrame"></div>
        </div>
        <div class="vf-checks" id="vfChecks"></div>
      </div>
    </div>

    <div class="vf-status" id="vfStatus"></div>
  `;

  root.querySelector('#vfSetupEdit').addEventListener('click', () => {
    setupOpen = true;
    syncSetup();
  });

  renderNetworks();
  wireDrop();
  wirePreview();
  renderBuild();
  renderPreview();
  renderChecks();
  syncSetup();
  updateStatus();
}

// ===== 1. Network picker =====
function renderNetworks() {
  const el = root.querySelector('#vfNetworks');
  el.innerHTML = NETWORKS.map(n => `
    <label class="cv-net ${selectedNetwork === n.key ? 'on' : ''}" data-key="${n.key}">
      <input type="radio" name="vfNetwork" value="${n.key}" ${selectedNetwork === n.key ? 'checked' : ''}>
      <span class="cv-net-dot ${n.key}"></span>
      <span>${escapeHtml(n.name)}</span>
    </label>`).join('');

  el.querySelectorAll('.cv-net').forEach(label => {
    label.querySelector('input').addEventListener('change', () => {
      selectedNetwork = label.dataset.key;
      el.querySelectorAll('.cv-net').forEach(l =>
        l.classList.toggle('on', l.dataset.key === selectedNetwork));
      syncDropState();
      renderPreview();      // re-runs the build with the new network's probe hooks
      renderChecks();
      syncSetup();
      updateStatus();
    });
  });
}

// ===== 2. Upload =====
// The network decides which rules run and which SDK hooks the probe installs,
// so it has to be chosen before a file is accepted — loading first would mean
// re-running the build the moment the network changed.
function syncDropState() {
  const drop = root.querySelector('#vfDrop');
  if (drop) drop.classList.toggle('locked', !selectedNetwork);
}

function wireDrop() {
  const drop = root.querySelector('#vfDrop');
  const fileInput = root.querySelector('#vfFileInput');
  const openPicker = () => { if (selectedNetwork) fileInput.click(); };
  root.querySelector('#vfBrowse').addEventListener('click', (e) => { e.preventDefault(); openPicker(); });
  drop.addEventListener('click', (e) => {
    // The file input lives inside the dropzone, so the click openPicker() fires
    // on it bubbles straight back here. Ignoring it matters: re-entering would
    // preventDefault() the input's own click and cancel the file dialog.
    if (e.target === fileInput || e.target.closest('button')) return;
    openPicker();
  });
  fileInput.addEventListener('change', (e) => { loadFile(e.target.files[0]); e.target.value = ''; });
  ['dragenter', 'dragover'].forEach(ev =>
    drop.addEventListener(ev, (e) => {
      e.preventDefault();
      if (selectedNetwork) drop.classList.add('drag');
    }));
  ['dragleave', 'drop'].forEach(ev =>
    drop.addEventListener(ev, (e) => {
      e.preventDefault();
      if (ev === 'dragleave' && drop.contains(e.relatedTarget)) return;
      drop.classList.remove('drag');
    }));
  drop.addEventListener('drop', (e) => {
    e.preventDefault();
    if (selectedNetwork) loadFile(e.dataTransfer.files[0]);
  });
  syncDropState();
}

async function loadFile(file) {
  if (!file) return;
  build = null;
  loadError = null;

  try {
    if (/\.zip$/i.test(file.name)) {
      const files = await unzip(await file.arrayBuffer());
      const entry = findEntryHtml(files);
      if (!entry) throw new Error('ZIP 안에서 HTML 파일을 찾을 수 없습니다');
      build = {
        name: file.name, size: file.size, kind: 'zip',
        html: decodeText(entry.data), entryPath: entry.name, files,
      };
    } else if (/\.html?$/i.test(file.name)) {
      const data = new Uint8Array(await file.arrayBuffer());
      build = {
        name: file.name, size: file.size, kind: 'html',
        html: decodeText(data), entryPath: file.name, files: [{ name: file.name, data }],
      };
    } else {
      throw new Error('.html 또는 .zip 파일만 검증할 수 있습니다');
    }
  } catch (err) {
    loadError = err.message;
  }
  if (build) {
    setupOpen = false;
    // Bring the stage into view — a build loaded from a scrolled page would
    // otherwise sit below the fold, which is the whole thing we're avoiding.
    root.closest('.home-tab-panel')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  renderBuild();
  renderPreview();
  renderChecks();
  syncSetup();
  updateStatus();
}

// Size the stage to whatever room is actually left below it. A CSS-only
// `calc(100vh - <guess>)` has to assume how tall the header and the collapsed
// summary are, and was overflowing the panel by the amount it guessed wrong.
function fitStage() {
  const stage = root.querySelector('#vfStage');
  const panel = root.closest('.home-tab-panel');
  if (!stage || !panel) return;

  // Measure with the stage forced tall. scrollHeight floors at clientHeight
  // once the content fits, so measuring at the current size made every call
  // read "content == viewport" and shrink the frame again — a ratchet that ran
  // the frame down to its minimum and could never grow back. Overflowing on
  // purpose makes scrollHeight report the true content height.
  const keepScroll = panel.scrollTop;
  stage.style.setProperty('--vf-h', '2000px');
  const other = panel.scrollHeight - stage.getBoundingClientRect().height;
  const avail = panel.clientHeight - other;
  panel.scrollTop = keepScroll;

  // `other` doesn't depend on --vf-h, so one pass settles it. Clamped so a very
  // short viewport falls back to scrolling rather than an unusable frame.
  stage.style.setProperty('--vf-h', Math.round(Math.max(340, Math.min(640, avail))) + 'px');
}
window.addEventListener('resize', fitStage);

// Class toggle only — rebuilding this subtree would tear down the running
// iframe and restart the build every time the summary changed.
function syncSetup() {
  const el = root.querySelector('#vfSetup');
  if (!el) return;
  const open = setupOpen || !build;
  el.classList.toggle('collapsed', !open);
  fitStage();
  if (open) return;

  const net = NETWORKS.find(n => n.key === selectedNetwork);
  root.querySelector('#vfSumNet').innerHTML = net
    ? `<span class="cv-net-dot ${net.key}"></span>${escapeHtml(net.name)}`
    : '';
  const detail = build.kind === 'zip'
    ? `${build.files.length}개 파일 · ${build.entryPath}`
    : fmtSize(build.size);
  root.querySelector('#vfSumFile').textContent = `${build.name} · ${detail}`;
}

function renderBuild() {
  const el = root.querySelector('#vfFile');
  if (!el) return;

  if (loadError) {
    el.innerHTML = `<div class="cv-chip err"><span class="cv-chip-name">⚠ ${escapeHtml(loadError)}</span></div>`;
    return;
  }
  if (!build) { el.innerHTML = ''; return; }

  // For a ZIP, the entry document and the file count are what tell the user we
  // opened the right thing — the archive name alone doesn't.
  const detail = build.kind === 'zip'
    ? `${build.files.length}개 파일 · ${build.entryPath}`
    : fmtSize(build.size);

  el.innerHTML = `
    <div class="cv-chip">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
      <span class="cv-chip-name">${escapeHtml(build.name)}</span>
      <span class="cv-chip-meta">${escapeHtml(detail)}</span>
      <button class="cv-chip-x" id="vfClear" title="제거">×</button>
    </div>`;
  el.querySelector('#vfClear').addEventListener('click', () => {
    build = null;
    setupOpen = true;
    renderBuild();
    renderPreview();
    renderChecks();
    syncSetup();
    updateStatus();
  });
}

// ===== 3. Preview =====
const MIME = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml', mp3: 'audio/mpeg', ogg: 'audio/ogg',
  wav: 'audio/wav', mp4: 'video/mp4', js: 'text/javascript', css: 'text/css',
  json: 'application/json', wasm: 'application/wasm',
};
function mimeOf(name) {
  return MIME[name.split('.').pop().toLowerCase()] || 'application/octet-stream';
}

function wirePreview() {
  root.querySelector('#vfReload').addEventListener('click', renderPreview);
}

// A blob: document can't resolve relative paths against the archive, so every
// asset gets its own blob URL and the entry HTML's references are rewritten to
// point at them. Only static references in the markup are caught — a path the
// build assembles at runtime still won't resolve.
function wireAssets(html) {
  const others = build.files.filter(f => f.name !== build.entryPath);
  if (!others.length) return html;

  const dir = build.entryPath.includes('/')
    ? build.entryPath.slice(0, build.entryPath.lastIndexOf('/') + 1) : '';

  for (const f of others) {
    const url = URL.createObjectURL(new Blob([f.data], { type: mimeOf(f.name) }));
    previewUrls.push(url);
    const rel = dir && f.name.startsWith(dir) ? f.name.slice(dir.length) : f.name;
    // Longest form first — replacing "a/b.png" before "./a/b.png" would leave
    // a stray "./" glued to the front of the blob URL.
    const forms = [...new Set([`./${rel}`, `/${rel}`, rel, f.name])]
      .sort((a, b) => b.length - a.length);
    for (const form of forms) html = html.split(form).join(url);
  }
  return html;
}

function releasePreview() {
  previewUrls.forEach(URL.revokeObjectURL);
  previewUrls = [];
}

function renderPreview() {
  const frame = root.querySelector('#vfFrame');
  if (!frame) return;

  releasePreview();
  fitStage();

  if (!build) {
    frame.innerHTML = `<div class="vf-frame-empty">빌드 파일을 추가하면<br>여기에서 실행됩니다</div>`;
    return;
  }

  const rules = rulesFor(selectedNetwork);
  runtime = freshRuntime();
  lastStatus = new Map();
  renderChecks();

  let doc = wireAssets(build.html);
  // `provide` installs container stubs (e.g. MRAID) the creative expects the ad
  // SDK to have put on window; `hooks` wrap entry points the build defines itself.
  if (rules) doc = injectProbe(doc, rules.hooks || [], rules.provide || [], { mraidVersion: rules.mraidVersion });
  const url = URL.createObjectURL(new Blob([doc], { type: 'text/html' }));
  previewUrls.push(url);

  // allow-same-origin so the build's localStorage/IndexedDB calls behave as they
  // do in a real webview — under an opaque origin they throw and the creative
  // dies on load, which would read as a failed build rather than a sandbox limit.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups');
  iframe.setAttribute('allow', 'autoplay; fullscreen');
  iframe.src = url;
  frame.replaceChildren(iframe);
}

// ===== 4. Checks =====
// Only the frame we are currently previewing may report. Anything else on the
// page could postMessage the same shape and quietly turn a check green.
window.addEventListener('message', (e) => {
  const m = e.data;
  if (!m || m.__pawVerify !== 1 || !runtime) return;
  const iframe = root?.querySelector('#vfFrame iframe');
  if (!iframe || e.source !== iframe.contentWindow) return;

  switch (m.type) {
    case 'boot':    runtime.booted = true; break;
    case 'gesture': runtime.gesture = true; runtime.gestures = m.data?.n ?? runtime.gestures + 1; break;
    case 'ready':   runtime.ready.add(m.data.id); break;
    case 'call':
      runtime.calls.add(m.data.id);
      if (!runtime.callInfo.has(m.data.id)) runtime.callInfo.set(m.data.id, m.data);
      break;
    case 'audio':   runtime.audio.push(m.data); break;
    case 'error':   runtime.errors.push(m.data); break;
    case 'autoplay-blocked': runtime.autoplayBlocked++; break;
    case 'net':     runtime.net.push(m.data); break;
    default: return;
  }
  renderChecks();
});

// Static rules answer now; runtime rules answer only once the build has shown
// the behaviour. "pending" is a real state here — not a soft pass.
function evalRuntime(item) {
  if (!runtime?.booted) return { status: 'pending', note: '빌드 실행 대기 중' };

  switch (item.id) {
    case 'exitapi-live':
      return runtime.ready.has('cta')
        ? { status: 'pass', note: 'window.ExitApi.exit 확인됨' }
        : { status: 'pending', note: '아직 정의되지 않음 — 로딩이 끝나면 갱신됩니다' };
    case 'smx-load':
      return runtime.calls.has('smx-load')
        ? { status: 'pass', note: 'load 이벤트 발생' }
        : { status: 'pending', note: '아직 load 보고 없음 — 로딩이 끝나면 갱신됩니다' };
    case 'smx-exclusive': {
      const d = runtime.callInfo.get('smx-download');
      const c = runtime.callInfo.get('smx-complete');
      if (!d || !c) {
        // Only meaningful once both have happened; one alone cannot violate it.
        return { status: 'pending', note: `발생: ${[d && 'download', c && 'complete'].filter(Boolean).join(', ') || '없음'}` };
      }
      const gap = Math.abs((d.t || 0) - (c.t || 0));
      return gap > 150
        ? { status: 'pass', note: `${gap}ms 간격 — 분리되어 발화` }
        : { status: 'fail', note: `${gap}ms 간격 — 함께 발화했습니다` };
    }
    case 'fb-telemetry': {
      // Deliberately not "did FbPlayableAd get touched" — the CTA check already
      // answers that. Luna's Meta logging (logGameLoad / logLevelComplete /
      // logButtonClick) is gated on the pi module's adNetwork being "facebook",
      // and pi is constructed with no arguments, so on these builds it never
      // fires and Meta receives no engagement telemetry at all.
      const logs = ['fb-gameload', 'fb-level', 'fb-button', 'fb-init']
        .filter(id => runtime.calls.has(id));
      if (logs.length) return { status: 'pass', note: logs.join(', ') + ' 보고됨' };
      if (runtime.calls.has('cta'))
        return { status: 'warn', note: 'CTA만 연결됨 — 로드·레벨 이벤트가 보고되지 않습니다' };
      return { status: 'pending', note: '재생하며 확인됩니다' };
    }
    case 'mtg-gameready-live':
      return runtime.calls.has('gameready')
        ? { status: 'pass', note: '빌드가 gameReady() 를 호출했습니다' }
        : { status: 'pending', note: '아직 준비 완료 보고 없음 — 로딩이 끝나면 갱신됩니다' };
    case 'mtg-gamestart-live':
      if (runtime.calls.has('gamestart')) return { status: 'pass', note: '플레이어가 gameStart() 로 시작시켰습니다' };
      // The creative pauses itself until the player starts it, so a build that
      // reported ready and then never started is stuck, not merely slow.
      return runtime.calls.has('gameready')
        ? { status: 'fail', note: 'gameReady 이후 gameStart 가 걸리지 않았습니다' }
        : { status: 'pending', note: '준비 완료 보고를 기다리는 중' };
    case 'mraid-use':
      return runtime.calls.has('mraid-use')
        ? { status: 'pass', note: '빌드가 mraid API를 호출했습니다' }
        : { status: 'pending', note: '아직 mraid 호출 없음 — 로딩이 끝나면 갱신됩니다' };
    case 'cta': {
      const c = runtime.callInfo.get('cta');
      if (c) return { status: 'pass', note: (c.args?.[0] || '호출 확인').slice(0, 60) };
      return { status: 'pending', note: '미리보기에서 CTA를 눌러 확인하세요' };
    }
    case 'first-tap': {
      const c = runtime.callInfo.get('cta');
      if (!c) return { status: 'pending', note: 'CTA가 발생하면 판정됩니다' };
      // The rule targets builds where any tap exits. If the tester's very first
      // input was the CTA itself this reads as a violation, so the note says
      // which input it was rather than just failing flat.
      return (c.gestures ?? 0) > 1
        ? { status: 'pass', note: `${c.gestures}번째 입력에서 발생` }
        : { status: 'fail', note: '첫 입력에서 바로 스토어로 이동했습니다' };
    }
    case 'no-network': {
      if (!runtime.net.length) {
        return runtime.gesture
          ? { status: 'pass', note: '외부 호출 없음' }
          : { status: 'pending', note: '아직 입력 전 — 플레이하며 확인됩니다' };
      }
      const hosts = [...new Set(runtime.net.map(n => { try { return new URL(n.url).host; } catch { return n.url; } }))];
      return { status: item.severity || 'fail', note: `${runtime.net.length}건 · ${hosts.slice(0, 3).join(', ')}` };
    }
    case 'audio': {
      const early = runtime.audio.filter(a => !a.gesture && !a.muted && a.volume > 0);
      if (early.length) return { status: 'fail', note: `입력 전 재생 ${early.length}건 (${early[0].kind})` };
      // The browser blocking an autoplay attempt is the rule working, so it
      // settles this check on its own — no tap needed to prove silence.
      if (runtime.autoplayBlocked) return { status: 'pass', note: `브라우저가 자동재생 차단 (${runtime.autoplayBlocked}건)` };
      return runtime.gesture
        ? { status: 'pass', note: '입력 전 재생 없음' }
        : { status: 'pending', note: '아직 입력 전 — 화면을 한 번 탭하세요' };
    }
    case 'noerror':
      return runtime.errors.length
        ? { status: 'fail', note: `${runtime.errors.length}건 · ${runtime.errors[0].message}`.slice(0, 120) }
        : { status: 'pass', note: '오류 없음' };
    default:
      return { status: 'pending' };
  }
}

// Which collapsed groups are open, kept across re-renders — renderChecks() runs
// on every probe message, and a list that reopened each time would be unusable.
let openGroups = new Set();

const CARET = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';

const ICONS = {
  warn: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  pass: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
  fail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
  pending: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>',
};

// Previous verdict per item, so a re-render can tell what actually changed.
// renderChecks() rebuilds the list on every probe message; without this the
// rows would repaint silently and results would never read as arriving.
let lastStatus = new Map();

function checkRow(item, res, big) {
  const changed = lastStatus.has(item.id) && lastStatus.get(item.id) !== res.status;
  lastStatus.set(item.id, res.status);
  return `<div class="vf-check ${res.status}${changed ? ' settled' : ''}${big ? ' big' : ''}" title="${escapeHtml(item.spec || '')}">
    <span class="vf-check-icon">${ICONS[res.status] || ICONS.pending}</span>
    <span class="vf-check-label">${escapeHtml(item.label)}</span>
    <span class="vf-check-note">${escapeHtml(res.note || '')}</span>
  </div>`;
}

// A settled group folds to one line. These resolve the moment the file loads
// and never move again, so they shouldn't crowd the runtime checks the user is
// actively driving.
function group(key, label, rows) {
  const nFail = rows.filter(x => x.res.status === 'fail').length;
  const nWarn = rows.filter(x => x.res.status === 'warn').length;
  const status = nFail ? 'fail' : nWarn ? 'warn' : 'pass';
  const note = nFail ? `${nFail}개 실패 / ${rows.length}개 중`
    : nWarn ? `${nWarn}개 확인 필요 / ${rows.length}개 중`
    : `${rows.length}개 모두 통과`;
  return `<div class="vf-static ${openGroups.has(key) ? 'open' : ''}">
    <button type="button" class="vf-static-row ${status}" data-group="${key}">
      <span class="vf-check-icon">${ICONS[status]}</span>
      <span class="vf-check-label">${escapeHtml(label)}</span>
      <span class="vf-check-note">${note}</span>
      <span class="vf-static-caret">${CARET}</span>
    </button>
    <div class="vf-static-detail">${rows.map(x => checkRow(x.item, x.res)).join('')}</div>
  </div>`;
}

function renderChecks() {
  const el = root.querySelector('#vfChecks');
  if (!el) return;

  const rules = rulesFor(selectedNetwork);
  if (!rules) {
    el.innerHTML = `<div class="vf-checks-empty">${selectedNetwork
      ? '이 네트워크의 검증 항목은 준비 중입니다'
      : '네트워크를 선택하면 검증 항목이 표시됩니다'}</div>`;
    return;
  }
  if (!build) {
    el.innerHTML = `<div class="vf-checks-empty">빌드 파일을 추가하면 ${escapeHtml(rules.label)} 항목을 검사합니다</div>`;
    return;
  }

  // One parse shared by every rule — reparsing a 3 MB document per rule is slow
  // enough to be felt on each probe message.
  const ctx = {
    html: build.html, size: build.size, files: build.files,
    kind: build.kind, entryPath: build.entryPath, name: build.name,
    // A creative can arrive wrapped in document.write("…") — a DSP delivery
    // shape one of the shipped ironSource files uses — which leaves every
    // quote in its code escaped as \". Rules that match quoted tokens read
    // this unescaped view so the wrapper doesn't hide what they look for.
    code: build.html.replace(/\\(["'])/g, '$1'),
    doc: new DOMParser().parseFromString(build.html, 'text/html'),
  };

  const statics = rules.static.map(item => {
    let res;
    try { res = item.run(ctx); }
    catch (err) { res = { status: 'fail', note: '검사 실패: ' + err.message }; }
    return { item, res };
  });
  const runtimes = (rules.runtime || []).map(item => ({ item, res: evalRuntime(item) }));
  const targetings = (rules.targeting || []).map(item => {
    let res;
    try { res = item.run(ctx); }
    catch (err) { res = { status: 'fail', note: '검사 실패: ' + err.message }; }
    return { item, res };
  });
  const all = [...statics, ...targetings, ...runtimes];
  const n = (st) => all.filter(x => x.res.status === st).length;

  el.innerHTML = `
    <div class="vf-checks-head">
      <span class="vf-tally">
        <b class="pass">통과 ${n('pass')}</b> · <b class="fail">실패 ${n('fail')}</b>${n('warn') ? ` · <b class="warn">확인 ${n('warn')}</b>` : ''} · <b>대기 ${n('pending')}</b>
      </span>
      <span class="vf-source">${escapeHtml(rules.source)}</span>
    </div>
    ${group('static', '사양 검사', statics)}
    ${targetings.length ? group('targeting', rules.targetingLabel || '타게팅 검사', targetings) : ''}

    <div class="vf-check-group">실행 검사 · 미리보기에서 플레이하며 확인</div>
    <div class="vf-runtime">${runtimes.map(x => checkRow(x.item, x.res, true)).join('')}</div>
  `;

  el.querySelectorAll('[data-group]').forEach(btn => {
    btn.addEventListener('click', () => {
      const k = btn.dataset.group;
      openGroups.has(k) ? openGroups.delete(k) : openGroups.add(k);
      renderChecks();
    });
  });
}

function updateStatus() {
  const el = root.querySelector('#vfStatus');
  if (!el) return;
  const net = NETWORKS.find(n => n.key === selectedNetwork);
  if (!net) el.textContent = '검증할 네트워크를 선택하세요';
  else if (!build) el.textContent = `${net.name} 규격으로 검증합니다 · 빌드 파일을 추가하세요`;
  else el.textContent = `${net.name} · ${build.name} · ${fmtSize(build.size)} 준비됨`;
}

// Lazy init — build the UI on first sub-tab open, not at startup.
let vfInited = false;
document.querySelector('#cvSubtabs .cv-subtab[data-cv-subtab="verify"]')?.addEventListener('click', () => {
  if (vfInited) return;
  vfInited = true;
  render();
});
